/* eslint-disable no-restricted-syntax,no-continue */

import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { authSelector, currentCohortPathSelector } from '../Auth/selectors';
import { currentUserSelector, currentUserIsEducator } from '../Users/selectors';
import {
  selectIsAdminOfCurrentCourse,
  selectCurrentCoursePath,
  selectCurrentCourseSubscriptionId,
} from '../Courses/selectors';
import { selectCurrentCourseNavigationInfo } from '../CourseNavigationInfo/selectors';
import { selectCurrentCourseCertification } from '../Certification/selectors';
import { PAID_CERTIFICATE_SET } from '../Certification/constants';

const cohortRolesSelector = (state) => state.CohortRoles;
const usersSelector = (state) => state.Users;

export const cohortRolesByCourseSelector = createSelector(
  cohortRolesSelector,
  (roles) => {
    let rolesByCourse = Immutable.Map();

    for (const role of roles.values()) {
      rolesByCourse = rolesByCourse.setIn(
        [role.get('course'), role.get('id')],
        role
      );
    }

    return rolesByCourse;
  }
);

export const currentCourseCohortRolesSelector = createSelector(
  selectCurrentCoursePath,
  cohortRolesByCourseSelector,
  (currentCourse, courseRoles) => {
    return courseRoles.get(currentCourse, Immutable.Map());
  }
);

export const currentCourseStaffSelector = createSelector(
  currentCourseCohortRolesSelector,
  usersSelector,
  (cohortRoles, users) => {
    let courseStaff = Immutable.OrderedMap();

    for (const role of cohortRoles.values()) {
      const userId = role.get('user');
      const cohortPath = role.get('cohort');

      if (role.get('roles').size === 0) {
        continue;
      }

      if (!courseStaff.has(userId)) {
        courseStaff = courseStaff.set(
          userId,
          users.get(userId, Immutable.Map())
        );
      }
      courseStaff = courseStaff.setIn(
        [userId, 'cohortRoles', cohortPath],
        role
      );
    }

    courseStaff = courseStaff.sortBy((rolesForUser) =>
      rolesForUser.get('fullName')
    );
    return courseStaff;
  }
);

export const selectCurrentUserRoleInCurrentCohort = createSelector(
  cohortRolesSelector,
  currentCohortPathSelector,
  currentUserSelector,
  (cohortRoles, cohortPath, currentUser) => {
    return cohortRoles.find((role) => {
      return (
        role.get('cohort') === cohortPath &&
        role.get('user') === currentUser.get('id')
      );
    });
  }
);

export const selectCurrentUserIsAssessorInCurrentCohort = createSelector(
  selectCurrentUserRoleInCurrentCohort,
  (role) => {
    return !!role && role.get('roles').indexOf('assessor') !== -1;
  }
);

export const selectCurrentUserIsAssessmentDesignerInCurrentCohort = createSelector(
  selectCurrentUserRoleInCurrentCohort,
  (role) => {
    return !!role && role.get('roles').indexOf('assessment_designer') !== -1;
  }
);

export const selectCurrentUserIsStaffInCurrentCohort = createSelector(
  selectCurrentUserRoleInCurrentCohort,
  (role) => {
    return !!role && role.get('roles').size > 0;
  }
);

export const selectCurrentUserShouldSeeCertificationInCurrentCohort = createSelector(
  selectCurrentUserIsAssessmentDesignerInCurrentCohort,
  selectIsAdminOfCurrentCourse,
  selectCurrentCourseNavigationInfo,
  (isAssessmentDesigner, isAdmin, navigationInfo) => {
    return !!(
      navigationInfo &&
      navigationInfo.get('showCertification') &&
      (isAssessmentDesigner || isAdmin)
    );
  }
);

export const selectGetCertifiedLinkVisible = createSelector(
  selectCurrentCourseNavigationInfo,
  selectCurrentCourseCertification,
  (navigationInfo, certification) => {
    return (
      navigationInfo &&
      navigationInfo.get('braintreeEnabled') &&
      certification &&
      PAID_CERTIFICATE_SET.has(certification.get('type'))
    );
  }
);

export const currentUserRolesSelector = createSelector(
  authSelector,
  selectCurrentCoursePath,
  currentCohortPathSelector,
  (auth, currentCoursePath, currentCohortPath) => {
    let roles = Immutable.List();

    if (auth.get('isSuperUser', false)) {
      roles = roles.push('superuser');
    }

    // add course roles
    roles = roles.concat(
      auth.getIn(['courseRoles', currentCoursePath], Immutable.List())
    );

    // add cohort roles
    roles = roles.concat(
      auth.getIn(['classRoles', currentCohortPath], Immutable.List())
    );

    return roles;
  }
);

export const selectCurrentUserIsEducatorInCurrentCourse = createSelector(
  currentUserIsEducator,
  selectCurrentCourseSubscriptionId,
  (isEducator, courseSubscriptionId) => {
    // a course under an educator plan does not have a linked subscriptionId
    return isEducator && !courseSubscriptionId;
  }
);
